import { useCallback, useMemo } from 'react'
import { defineSteps, step } from 'flows/common/helpers/defineSteps'
import { BrandPlanAwarenessRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/BrandPlanAwarenessRedesign'
import { BrandPlanTactics } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { BrandPlanTacticsRedesign } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTacticsRedesign'
import { BrandProfileStep } from 'components/FirstTimeExperienceModal/steps/BrandProfile/BrandProfileStep'
import { BrandProfileWebsite } from 'screens/VirtualMarketerOnboarding/BrandProfileWebsite/BrandProfileWebsite'

import { FlowCtx } from 'flows/common/machines/createFlowMachine'
import { StepProps } from 'flows/virtualMarketerOnboarding/Types'
import { CampaignDetails } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignDetails'
import {
  CampaignGenerationLoadingScreen,
  CampaignGenerationLoadingProvider,
} from 'components/FirstTimeExperienceModal/steps/CampaignGeneration'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { AutopilotSidebar as ProfileAutopilotSidebar } from 'components/FirstTimeExperienceModal/steps/BrandProfile/AutopilotSidebar'
import { ScreenshotFallback } from 'screens/VirtualMarketerOnboarding/ScreenshotFallback/ScreenshotFallback'
import { MobilePushToApp } from 'screens/VirtualMarketerOnboarding/MobilePushToApp/MobilePushToApp'
import { VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'
import { generatePath } from 'react-router-dom'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { NoWebsite } from 'screens/VirtualMarketerOnboarding/NoWebsite/NoWebsite'
import { BrandVisualStyleSelection } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection'
import { BrandVisualStylePreview } from 'screens/VirtualMarketerOnboarding/BrandVisualStylePreview'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { BrandVisualStyleFonts } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleFonts/BrandVisualStyleFonts'
import { BrandVisualStyleVoiceAndLogo } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleVoiceAndLogo/BrandVisualStyleVoiceAndLogo'
import { useIsPioneerStarterOrGrowth } from 'hooks/useIsPioneerStarterOrGrowth'
import { useChooseOnboardingPlan } from './useChooseOnboardingPlan'
import { useExperiment } from 'utils/experiments'
import { StrategiesSelection } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/StrategiesSelection'

const useOnboardingStrategiesEnabled = () => {
  const experimentVariant = useExperiment('onboarding_strategies')
  const { isMobile } = useResponsiveDesign()

  return !isMobile && experimentVariant === 'on'
}

const BrandProfileWebsiteWrapper = ({ send, context }: any) => {
  let effectiveBrandKitId = context.brandKitId
  if (!context.isIntentionalBrandKit) {
    effectiveBrandKitId = null
  }

  return (
    <BrandProfileWebsite
      websites={context.websites}
      language={context.language || context.defaultLanguage}
      brandKitId={effectiveBrandKitId}
      onLanguageChange={(language) => {
        send({ type: 'MERGE_CTX', payload: { language } })
      }}
      onBack={() => send({ type: 'BACK' })}
      onNext={async (brandProfile, brandVisualEnhancementId) => {
        let analysisId = brandProfile.latest_analysis_id

        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId,
            brandVisualEnhancementId,
            websiteScrapeSucceeded: true,
          },
        })
      }}
      onNoWebsite={() => {
        send({ type: 'NO_WEBSITE' })
      }}
    />
  )
}

const ScreenshotFallbackWrapper = ({ send, context }: StepProps) => {
  return (
    <ScreenshotFallback
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
            websiteScrapeSucceeded: true,
          },
        })
      }}
    />
  )
}

const NoWebsiteWrapper = ({ send, context }: StepProps) => {
  return (
    <NoWebsite
      onBack={() => send({ type: 'BACK' })}
      brandKitId={context.brandKitId}
      url={context.websiteUrl}
      selectedLanguage={context.selectedLanguage}
      onNext={async (brandProfile) => {
        send({
          type: 'NEXT',
          payload: {
            brandProfileId: brandProfile.id,
            brandKitId: brandProfile.brand_kit.id,
            websiteUrl: brandProfile.url,
            brandPlanId: brandProfile.brand_plan.id,
            analysisId: brandProfile.latest_analysis_id,
          },
        })
      }}
    />
  )
}

const BrandProfileStepWrapper = ({ send, context }: StepProps) => {
  const { data: brandKitData } = useBrandKit(context.brandKitId, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: context.brandProfileId })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const handleBack = useCallback(
    (websiteScrapeSucceeded = true) => {
      deleteBrandProfile()
      const brandKitSources = brandKitData?.brand_kit?.brand_kit_sources
      brandKitSources?.forEach((source) => {
        context.brandKitId && deleteBrandKitSource({ brandKitId: context.brandKitId, id: source.id })
      })

      send({
        type: 'MERGE_CTX',
        payload: {
          brandProfileId: null,
          brandProfile: null,
          skipUploadBrandMaterials: false,
          brandKitId: context.isIntentionalBrandKit ? context.brandKitId : null,
          isIntentionalBrandKit: context.isIntentionalBrandKit || false,
          profile: null,
          websiteScrapeSucceeded,
        },
      })

      send({ type: 'BACK' })
    },
    [
      deleteBrandProfile,
      brandKitData?.brand_kit?.brand_kit_sources,
      context.brandKitId,
      context.isIntentionalBrandKit,
      deleteBrandKitSource,
      send,
    ],
  )

  const { saveSubscriptionPlan } = useChooseOnboardingPlan()

  return (
    <BrandProfileStep
      analysisId={context.analysisId}
      id={context.brandProfileId}
      initialProfile={context.profile}
      deleteOnBack={false}
      onAnalysis={(analysisId) => {
        send({ type: 'MERGE_CTX', payload: { analysisId } })
      }}
      onNext={(profile) => {
        saveSubscriptionPlan()

        send({
          type: 'MERGE_CTX',
          payload: { profile },
        })
        if (context.isMobile) {
          send({ type: 'IS_MOBILE_NEXT' })
        } else {
          send({ type: 'NEXT' })
        }
      }}
      onBack={handleBack}
    />
  )
}

const MidFlowMobilePushToAppWrapper = ({ send, context }: StepProps) => {
  const { isIOSNative } = useResponsiveDesign()

  if (isIOSNative) {
    send({ type: 'NEXT' })
    return null
  }

  const desktopPath = generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId: context.currentWorkspaceId })

  return <MobilePushToApp isMidFlow desktopPath={desktopPath} onContinue={() => send({ type: 'NEXT' })} />
}

const BrandPlanAwarenessWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for BrandPlanAwarenessStep')
  }

  const isStarterPlan = planVariant === 'starter'

  const handleNext = (payload?: any) => {
    if (payload?.desktopPath && payload?.publishDay) {
      send({ type: 'MOBILE_COMPLETE', payload })
    } else {
      send({ type: 'NEXT', payload })
    }
  }

  return (
    <BrandPlanAwarenessRedesign
      id={context.brandPlanId}
      selectedBlogPlatform={context.selectedBlogPlatform}
      selectedEmailPlatform={context.selectedEmailPlatform}
      isStarterPlan={isStarterPlan}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const WhereToPublishWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  const planVariant = useIsPioneerStarterOrGrowth()
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for WhereToPublish')
  }

  const mode = planVariant === 'starter' ? 'starter' : 'campaign'

  const handleBack = () => {
    if (isOnboardingAddVoicePageFlag) {
      send({ type: 'BACK_TO_VISUAL_STYLES_VOICE' })
    } else {
      send({ type: 'BACK_TO_FONTS' })
    }
  }

  const handleNext = ({
    tactics,
    startDate,
    selectedBlogPlatform,
    selectedEmailPlatform,
    paidAdsOnboardingParameters,
  }: any) => {
    const payload = { tactics, startDate, selectedBlogPlatform, selectedEmailPlatform, paidAdsOnboardingParameters }

    if (planVariant === 'starter') {
      send({ type: 'NEXT_STARTER', payload })
    } else {
      send({ type: 'NEXT_GROWTH', payload })
    }
  }

  // Starter flow uses the redesigned tactics page
  if (mode === 'starter') {
    return (
      <BrandPlanTacticsRedesign
        id={context.brandPlanId}
        mode={mode}
        tactics={context.tactics || []}
        seoKeywords={context.seoKeywords || []}
        idealCustomers={context.idealCustomers || []}
        onBack={handleBack}
        onProvideBackHandler={onProvideBackHandler}
        onNext={handleNext}
      />
    )
  }

  // Growth/campaigns flow uses the original tactics component
  return (
    <BrandPlanTactics
      id={context.brandPlanId}
      mode={mode}
      externalIntegrations={context.externalIntegrations}
      brandKitId={context.brandKitId}
      tactics={context.tactics}
      seoKeywords={context.seoKeywords}
      idealCustomers={context.idealCustomers}
      initialInsightChannels={context.insightChannels || []}
      onBack={handleBack}
      onProvideBackHandler={onProvideBackHandler}
      onNext={handleNext}
    />
  )
}

const CampaignGenerationLoadingWrapper = ({ send, context }: StepProps) => {
  if (!context.brandPlanId) {
    throw new Error('Brand plan ID is required for CampaignGenerationLoading')
  }

  return (
    <CampaignGenerationLoadingProvider>
      <CampaignGenerationLoadingScreen
        brandPlanId={context.brandPlanId}
        campaignData={context.campaignData}
        startDate={context.startDate}
        onComplete={() => send({ type: 'COMPLETE' })}
        onBack={() => send({ type: 'BACK' })}
      />
    </CampaignGenerationLoadingProvider>
  )
}

const CampaignDetailsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  if (!context.brandPlanId || !context.brandKitId) {
    throw new Error('Brand plan ID and brand kit ID are required for CampaignDetails')
  }

  return (
    <CampaignDetails
      brandPlanId={context.brandPlanId}
      brandKitId={context.brandKitId}
      initialValues={context.campaignData}
      paidAdsOnboardingParameters={context.paidAdsOnboardingParameters}
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      onNext={(campaignData) => {
        send({
          type: 'NEXT',
          payload: {
            campaignData: {
              growth_goals_input: campaignData.growth_goals_input,
              key_dates_input: campaignData.key_dates_input,
            },
            startDate: campaignData.startDate,
          },
        })
      }}
    />
  )
}

const BrandVisualStyleSelectionWrapper = ({ send, context }: StepProps) => {
  return (
    <BrandVisualStyleSelection
      brandKitId={context.brandKitId}
      onBack={() => send({ type: 'BACK' })}
      onNext={(fontPairings, stylesGenerated, sourceImage) => {
        send({
          type: 'MERGE_CTX',
          payload: { fontPairings, stylesGenerated, sourceImage },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const StrategiesSelectionWrapper = ({ send, context }: StepProps) => {
  return (
    <StrategiesSelection
      initialSelectedStrategies={context.brandPlanStrategies}
      selectedBusinessType={context.selectedBusinessType}
      goal={context.goal}
      onNext={(brandPlanStrategies) => {
        send({
          type: 'MERGE_CTX',
          payload: { brandPlanStrategies },
        })
        send({ type: 'NEXT' })
      }}
      onBack={() => send({ type: 'BACK' })}
    />
  )
}

const BrandVisualStylePreviewWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStylePreview
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      initialSelectedStyle={context.selectedVisualStyle}
      initialStylesGenerated={context.stylesGenerated}
      sourceImage={context.sourceImage}
      brandKitId={context.brandKitId}
      onNext={(selectedVisualStyle, stylesGenerated) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedVisualStyle, stylesGenerated },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleFontsWrapper = ({ send, context, onProvideBackHandler }: StepProps) => {
  return (
    <BrandVisualStyleFonts
      onBack={() => send({ type: 'BACK' })}
      onProvideBackHandler={onProvideBackHandler}
      brandKitId={context.brandKitId}
      initialFontPairings={context.fontPairings}
      initialFontPairingId={context.selectedFontPairingId}
      selectedStyle={context.selectedVisualStyle}
      onNext={(selectedFontPairingId) => {
        send({
          type: 'MERGE_CTX',
          payload: { selectedFontPairingId },
        })
        send({ type: 'NEXT' })
      }}
    />
  )
}

const BrandVisualStyleVoiceAndLogoWrapper = ({ send, context }: StepProps) => {
  const isOnboardingAddVoicePageFlag = useFlipperFlag('onboarding_add_voice_page')

  if (!isOnboardingAddVoicePageFlag || !context.brandKitId) {
    send({ type: 'NEXT' })
    return null
  }

  return (
    <BrandVisualStyleVoiceAndLogo
      onNext={() => {
        send({ type: 'NEXT' })
      }}
      onBack={() => {
        send({ type: 'BACK' })
      }}
      brandKitId={context.brandKitId}
    />
  )
}

const flowStep = step<FlowCtx>

const steps = [
  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper).back('website'),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('IS_MOBILE_NEXT', 'midFlowMobilePushToApp')
    .on('NEXT', 'brandVisualStyleSelection'),
  flowStep('midFlowMobilePushToApp', MidFlowMobilePushToAppWrapper)
    .on('NEXT', 'brandVisualStyleSelection')
    .skipWhen((ctx) => !ctx.isMobile),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('profile')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleVoiceAndLogo'),

  flowStep('brandVisualStyleVoiceAndLogo', BrandVisualStyleVoiceAndLogoWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT', 'whereToPublish'),

  flowStep('whereToPublish', WhereToPublishWrapper)
    .back('brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_VISUAL_STYLES_VOICE', 'brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_FONTS', 'brandVisualStyleFonts')
    .on('NEXT', 'awareness')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignDetails'),

  flowStep('campaignDetails', CampaignDetailsWrapper).back('whereToPublish').on('NEXT', 'campaignGenerationLoading'),

  flowStep('awareness', BrandPlanAwarenessWrapper)
    .back('whereToPublish')
    .on('NEXT', 'done')
    .on('MOBILE_COMPLETE', 'done'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'campaignDetails')
    .on('COMPLETE', 'done'),
]

const stepsWithStrategies = [
  flowStep('screenshotFallback', ScreenshotFallbackWrapper)
    .skipWhen((ctx) => ctx.websiteScrapeSucceeded)
    .back('website'),
  flowStep('noWebsite', NoWebsiteWrapper).back('website'),
  flowStep('profile', BrandProfileStepWrapper)
    .withSiderComponent(ProfileAutopilotSidebar)
    .back('website', (ctx) => ctx.websiteScrapeSucceeded)
    .back('screenshotFallback', (ctx) => !ctx.websiteScrapeSucceeded)
    .on('IS_MOBILE_NEXT', 'midFlowMobilePushToApp')
    .on('NEXT', 'strategiesSelection'),
  flowStep('midFlowMobilePushToApp', MidFlowMobilePushToAppWrapper)
    .on('NEXT', 'strategiesSelection')
    .skipWhen((ctx) => !ctx.isMobile),
  flowStep('strategiesSelection', StrategiesSelectionWrapper).back('profile').on('NEXT', 'brandVisualStyleSelection'),
  flowStep('brandVisualStyleSelection', BrandVisualStyleSelectionWrapper)
    .back('strategiesSelection')
    .on('NEXT', 'brandVisualStylePreview'),

  flowStep('brandVisualStylePreview', BrandVisualStylePreviewWrapper)
    .back('brandVisualStyleSelection')
    .on('NEXT', 'brandVisualStyleFonts'),

  flowStep('brandVisualStyleFonts', BrandVisualStyleFontsWrapper)
    .back('brandVisualStylePreview')
    .on('NEXT', 'brandVisualStyleVoiceAndLogo'),

  flowStep('brandVisualStyleVoiceAndLogo', BrandVisualStyleVoiceAndLogoWrapper)
    .back('brandVisualStyleFonts')
    .on('NEXT', 'whereToPublish'),

  flowStep('whereToPublish', WhereToPublishWrapper)
    .back('brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_VISUAL_STYLES_VOICE', 'brandVisualStyleVoiceAndLogo')
    .on('BACK_TO_FONTS', 'brandVisualStyleFonts')
    .on('NEXT', 'awareness')
    .on('NEXT_STARTER', 'awareness')
    .on('NEXT_GROWTH', 'campaignDetails'),

  flowStep('campaignDetails', CampaignDetailsWrapper).back('whereToPublish').on('NEXT', 'campaignGenerationLoading'),

  flowStep('awareness', BrandPlanAwarenessWrapper)
    .back('whereToPublish')
    .on('NEXT', 'done')
    .on('MOBILE_COMPLETE', 'done'),

  flowStep('campaignGenerationLoading', CampaignGenerationLoadingWrapper)
    .isFullScreen()
    .on('BACK', 'campaignDetails')
    .on('COMPLETE', 'done'),
]

const websiteStep = [
  flowStep('website', BrandProfileWebsiteWrapper)
    .withExtra((ctx) => ({
      createBrandProfileAnalysis: ctx.createBrandProfileAnalysis,
    }))
    .on('NO_WEBSITE', 'noWebsite')
    .on('NEXT', 'profile'),
]

export const virtualMarketerRedesignSteps = defineSteps<FlowCtx>([...websiteStep, ...steps])

export const useVirtualMarketerRedesignSteps = () => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()

  return useMemo(
    () => defineSteps<FlowCtx>([...websiteStep, ...(isOnboardingStrategiesEnabled ? stepsWithStrategies : steps)]),
    [isOnboardingStrategiesEnabled],
  )
}
